/**
 * Copyright (c) 2025, CheckPlus Limited
 */
import React, { Component } from "react";
import PropTypes from "prop-types";
import { loadStripe } from "@stripe/stripe-js";
import { CardElement, Elements, useStripe, useElements } from "@stripe/react-stripe-js";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { checkEmail } from "../../../assets/validators/common.js";
import { simpleFetch } from "../../../restWrapper";
import { SHOW_ERRORS, ErrorPreview } from "./ErrorPreview";
import { STRIPE_KEY_CHECKPLUS, PLATFORM } from "../../../config";
import "./Checkout.css";

// Always CheckPlus's Stripe account — every Marketplace donation destination
// is a nonprofit, regardless of which platform bundle built this page (this
// file ships inside the Plug bundle). A PaymentMethod tokenized under one
// Stripe account can't be handed to another, so this can't reuse the app's
// general-purpose STRIPE_KEY (CP-268 Phase 0).
const stripePromise = loadStripe(STRIPE_KEY_CHECKPLUS);
const POLL_INTERVAL = 2000;
const MAX_POLL_TIME = 300000;

// Real charge across one or more nonprofits, one-time gift only. One card,
// one basket POST (`/donors/basket-payments`) — the backend charges the
// donor once for the basket total and settles each nonprofit's share via a
// separate Stripe transfer (CP-268).
class Checkout extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: "details", // details | card | review | requires3DS | error
      // null is "nobody has touched this field", which is not the same as
      // "". An untouched field shows what the account already says; a field
      // emptied by hand stays empty, because "" is an answer somebody gave.
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      fieldErrors: {},
      orgRecords: {}, // keyed by planRow id
      orgLoading: true,
      orgError: false,
      submitting: false,
      errMsg: "",
      stripe3DS: "",
      paymentIntent: "",
      paymentMethodId: "",
      cardBrand: "",
      cardLast4: "",
      failedLegs: [],
    };
    this.pollTimer = null;
  }

  componentDidMount() {
    const rows = this.props.vm.planRows;
    Promise.all(
      rows.map((org) =>
        simpleFetch("/donors/organization", "GET", { subDomain: org.id, isCustomDomain: false }).then((res) => ({
          rowId: org.id,
          ok: res.status === 200 && res.body && res.body._id,
          record: res.body,
        }))
      )
    )
      .then((results) => {
        if (results.some((r) => !r.ok)) {
          this.setState({ orgLoading: false, orgError: true });
          return;
        }
        const orgRecords = {};
        results.forEach((r) => {
          orgRecords[r.rowId] = r.record;
        });
        this.setState({ orgLoading: false, orgRecords });
      })
      .catch(() => {
        this.setState({ orgLoading: false, orgError: true });
      });
  }

  componentWillUnmount() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
    }
  }

  /**
   * What one of the four detail fields holds: what has been typed into it,
   * or, until something has been, what a signed-in donor's account already
   * says (vm.payer — null for everybody else, which is what makes a
   * signed-out checkout ask for all four as it always has).
   *
   * Read through rather than copied into state at mount, so an account that
   * lands a moment after this form does still fills it, and so nothing has
   * to reconcile a copy with the record it came from.
   */
  value = (field) => {
    const typed = this.state[field];
    if (typed !== null) {
      return typed;
    }
    return (this.props.vm.payer || {})[field] || "";
  };

  onField = (field) => (e) => {
    this.setState({ [field]: e.target.value });
  };

  onPhone = (value) => {
    this.setState({ phone: value || "" });
  };

  validateDetails = () => {
    const m = this.props.vm.m;
    const errors = {};
    /* Validated the same whether it was typed or prefilled. An account with
       a half-filled record is a real case — phoneNumber is optional at
       sign-up — so a prefill is a head start, never a reason to skip the
       check. */
    if (!this.value("firstName").trim()) {
      errors.firstName = m.errFirstName;
    }
    if (!this.value("lastName").trim()) {
      errors.lastName = m.errLastName;
    }
    if (!checkEmail(this.value("email"))) {
      errors.email = m.errEmail;
    }
    const phone = this.value("phone");
    if (!phone || !isValidPhoneNumber(phone)) {
      errors.phone = m.errPhone;
    }
    this.setState({ fieldErrors: errors });
    return Object.keys(errors).length === 0;
  };

  continueToCard = () => {
    if (!this.validateDetails()) {
      return;
    }
    this.setState({ step: "card" });
  };

  // Tokenizes the card and moves to a review step — the actual charge only
  // fires from confirmAndPay(), after the donor sees a final summary.
  continueToReview = () => {
    const m = this.props.vm.m;
    const { stripe, elements } = this.props;
    if (!stripe || !elements || this.state.submitting) {
      return;
    }

    this.setState({ submitting: true, errMsg: "" });
    const cardElement = elements.getElement(CardElement);

    stripe
      .createPaymentMethod({
        type: "card",
        card: cardElement,
        billing_details: {
          name: this.value("firstName") + " " + this.value("lastName"),
          email: this.value("email"),
          phone: this.value("phone"),
        },
      })
      .then(({ paymentMethod, error }) => {
        if (error || !paymentMethod) {
          this.setState({
            submitting: false,
            step: "error",
            errMsg: (error && error.message) || m.errCard,
          });
          return;
        }
        this.setState({
          submitting: false,
          step: "review",
          paymentMethodId: paymentMethod.id,
          cardBrand: paymentMethod.card.brand,
          cardLast4: paymentMethod.card.last4,
        });
      })
      .catch(() => {
        this.setState({ submitting: false, step: "error", errMsg: m.errCard });
      });
  };

  confirmAndPay = () => {
    const m = this.props.vm.m;
    const vm = this.props.vm;
    if (this.state.submitting) {
      return;
    }
    const rows = vm.planRows;
    const missingOrg = rows.some((r) => !this.state.orgRecords[r.id] || !this.state.orgRecords[r.id]._id);
    if (missingOrg) {
      this.setState({ step: "error", errMsg: m.errOrgLoad });
      return;
    }

    this.setState({ submitting: true, errMsg: "" });
    const basket = rows.map((r) => ({
      organizationId: this.state.orgRecords[r.id]._id,
      amount: r.amountRaw,
    }));

    // NOTE: no `orgName`. The backend stores that as donorOrganizationName —
    // the company the *donor* gives on behalf of, the same field the subdomain
    // flow fills from the donor's "Organisation name" input. Sending the
    // recipient nonprofit's name there made every marketplace gift look like a
    // corporate donation from the nonprofit itself, in the supporter list and
    // on the receipt. This form only takes an individual's details, so the
    // field stays empty and payerType stays "individual".
    const paymentInfo = {
      basket,
      firstName: this.value("firstName"),
      lastName: this.value("lastName"),
      email: this.value("email"),
      phoneNumber: this.value("phone"),
      currency: "hkd",
      country: "HK",
      paymentMethod: this.state.paymentMethodId,
      coverFee: false,
      payerType: "individual",
      sourcePlatform: PLATFORM,
    };

    simpleFetch("/donors/basket-payments", "POST", paymentInfo).then((res) =>
      this.handlePaymentResponse(res, paymentInfo)
    );
  };

  handlePaymentResponse = (res, paymentInfo) => {
    const m = this.props.vm.m;
    const body = res.body || {};
    // "partial" means the charge succeeded but one or more legs' transfers
    // are still being retried server-side — the donor's card was charged in
    // full either way, so this still routes to the done screen.
    if (body.status === "succeeded" || body.status === "partial") {
      this.setState({ submitting: false, failedLegs: body.failedLegs || [] });
      this.props.vm.goDone();
      return;
    }
    if (body.status === "requires_action") {
      this.setState({
        submitting: false,
        step: "requires3DS",
        stripe3DS: body.stripe3DS,
        paymentIntent: body.paymentIntent,
      });
      this.pollPaymentStatus(paymentInfo);
      return;
    }
    this.setState({ submitting: false, step: "error", errMsg: body.message || m.errPayment });
  };

  pollPaymentStatus = (paymentInfo) => {
    const m = this.props.vm.m;
    let elapsed = 0;
    this.pollTimer = setInterval(() => {
      if (elapsed >= MAX_POLL_TIME) {
        clearInterval(this.pollTimer);
        this.setState({ step: "error", errMsg: m.err3DSTimeout });
        return;
      }
      elapsed += POLL_INTERVAL;

      simpleFetch("/donors/basket-payments/retrievePaymentStatus", "POST", { paymentIntent: this.state.paymentIntent })
        .then((res) => {
          if (res.body && res.body.status === "succeeded") {
            clearInterval(this.pollTimer);
            return simpleFetch("/donors/basket-payments/confirm3DSPayment", "POST", {
              ...paymentInfo,
              paymentIntent: this.state.paymentIntent,
            });
          }
          if (res.body && res.body.status === "requires_payment_method") {
            clearInterval(this.pollTimer);
            this.setState({ step: "error", errMsg: m.err3DSFailed });
          }
          return null;
        })
        .then((confirmRes) => {
          if (confirmRes && confirmRes.body) {
            this.setState({ failedLegs: confirmRes.body.failedLegs || [] });
            this.props.vm.goDone();
          }
        })
        .catch(() => {
          clearInterval(this.pollTimer);
          this.setState({ step: "error", errMsg: m.err3DSFailed });
        });
    }, POLL_INTERVAL);
  };

  // Shared summary block (mono icon + amount + recipient text) used by both
  // the details and card steps — works for one nonprofit or a basket of many.
  renderSummary() {
    const m = this.props.vm.m;
    const vm = this.props.vm;
    const org = vm.planRows[0];
    const toText =
      vm.planRows.length > 1 ? vm.fill(m.checkoutToMulti, { n: vm.planRows.length }) : vm.fill(m.checkoutTo, { org: org.name });
    return (
      <div className="Marketplace-Checkout-summary">
        <div className="Marketplace-Checkout-summaryMono" style={{ background: org.monoBg, color: org.monoFg }}>
          {org.logo ? <img src={org.logo} alt={org.name} className="Marketplace-Checkout-summaryMonoImg" /> : org.mono}
        </div>
        <div>
          <div className="Marketplace-Checkout-summaryAmount">{vm.monthlyLabel} {m.oneTime}</div>
          <div className="Marketplace-Checkout-summaryOrg">{toText}</div>
        </div>
      </div>
    );
  }

  renderDetailsStep() {
    const m = this.props.vm.m;
    const errors = this.state.fieldErrors;
    return (
      <div className="Marketplace-Checkout-card">
        {this.renderSummary()}
        <div className="Marketplace-Checkout-title">{m.checkoutYourDetails}</div>
        {/* Says where the four filled boxes came from, and that they can be
            changed. Without it a donor has to guess whether editing one here
            also rewrites their account — it does not. */}
        {!!this.props.vm.payer && (
          <div className="Marketplace-Checkout-prefilled">{m.checkoutPrefilled}</div>
        )}
        <div className="Marketplace-Checkout-row">
          <div className="Marketplace-Checkout-field">
            <label className="Marketplace-Checkout-label">{m.checkoutFirstName}</label>
            <input className="Marketplace-Checkout-input" value={this.value("firstName")} onChange={this.onField("firstName")} />
            {errors.firstName && <div className="Marketplace-Checkout-fieldErr">{errors.firstName}</div>}
            {SHOW_ERRORS && <ErrorPreview m={m} className="Marketplace-Checkout-fieldErr" keys={["errFirstName"]} />}
          </div>
          <div className="Marketplace-Checkout-field">
            <label className="Marketplace-Checkout-label">{m.checkoutLastName}</label>
            <input className="Marketplace-Checkout-input" value={this.value("lastName")} onChange={this.onField("lastName")} />
            {errors.lastName && <div className="Marketplace-Checkout-fieldErr">{errors.lastName}</div>}
            {SHOW_ERRORS && <ErrorPreview m={m} className="Marketplace-Checkout-fieldErr" keys={["errLastName"]} />}
          </div>
        </div>
        <div className="Marketplace-Checkout-field">
          <label className="Marketplace-Checkout-label">{m.checkoutEmail}</label>
          <input className="Marketplace-Checkout-input" value={this.value("email")} onChange={this.onField("email")} />
          {errors.email && <div className="Marketplace-Checkout-fieldErr">{errors.email}</div>}
          {SHOW_ERRORS && <ErrorPreview m={m} className="Marketplace-Checkout-fieldErr" keys={["errEmail"]} />}
        </div>
        <div className="Marketplace-Checkout-field">
          <label className="Marketplace-Checkout-label">{m.checkoutPhone}</label>
          <PhoneInput
            international
            defaultCountry="HK"
            className="Marketplace-Checkout-phoneInput"
            value={this.value("phone")}
            onChange={this.onPhone}
          />
          {errors.phone && <div className="Marketplace-Checkout-fieldErr">{errors.phone}</div>}
          {SHOW_ERRORS && <ErrorPreview m={m} className="Marketplace-Checkout-fieldErr" keys={["errPhone"]} />}
        </div>
        <div className="Marketplace-Checkout-btnPrimary" onClick={this.continueToCard}>
          {m.checkoutContinueToPayment}
        </div>
      </div>
    );
  }

  renderCardStep() {
    const m = this.props.vm.m;
    return (
      <div className="Marketplace-Checkout-card">
        {this.renderSummary()}
        <div className="Marketplace-Checkout-title">{m.checkoutCardDetails}</div>
        <div className="Marketplace-Checkout-cardBox">
          <CardElement options={{ style: { base: { fontSize: "15px" } } }} />
        </div>
        {this.state.errMsg && <div className="Marketplace-Checkout-fieldErr">{this.state.errMsg}</div>}
        {SHOW_ERRORS && <ErrorPreview m={m} className="Marketplace-Checkout-fieldErr" keys={["errCard"]} />}
        <div
          className={
            "Marketplace-Checkout-btnPrimary" + (this.state.submitting ? " Marketplace-Checkout-btnPrimary--disabled" : "")
          }
          onClick={this.state.submitting ? undefined : this.continueToReview}
        >
          {this.state.submitting ? m.checkoutProcessing : m.checkoutReviewGift}
        </div>
      </div>
    );
  }

  renderReviewStep() {
    const m = this.props.vm.m;
    const vm = this.props.vm;
    return (
      <div className="Marketplace-Checkout-card">
        <div className="Marketplace-Checkout-title">{m.checkoutReviewTitle}</div>
        <div className="Marketplace-Checkout-reviewRow">
          <span className="Marketplace-Checkout-reviewLabel">{m.checkoutAmount}</span>
          <span className="Marketplace-Checkout-reviewValue">{vm.monthlyLabel} {m.oneTime}</span>
        </div>
        <div className="Marketplace-Checkout-reviewRow">
          <span className="Marketplace-Checkout-reviewLabel">{m.checkoutToLabel}</span>
          <span className="Marketplace-Checkout-reviewValue">
            {vm.planRows.map((r) => (
              <div key={r.id}>
                {r.name} — {r.amount}
              </div>
            ))}
          </span>
        </div>
        <div className="Marketplace-Checkout-reviewRow">
          <span className="Marketplace-Checkout-reviewLabel">{m.checkoutFrom}</span>
          <span className="Marketplace-Checkout-reviewValue">
            {this.value("firstName")} {this.value("lastName")}
          </span>
        </div>
        <div className="Marketplace-Checkout-reviewRow">
          <span className="Marketplace-Checkout-reviewLabel">{m.checkoutContact}</span>
          <span className="Marketplace-Checkout-reviewValue">{this.value("email")}</span>
        </div>
        <div className="Marketplace-Checkout-reviewRow">
          <span className="Marketplace-Checkout-reviewLabel">{m.checkoutCard}</span>
          <span className="Marketplace-Checkout-reviewValue">
            {this.state.cardBrand} •••• {this.state.cardLast4}
          </span>
        </div>
        {this.state.errMsg && <div className="Marketplace-Checkout-fieldErr">{this.state.errMsg}</div>}
        <div
          className={
            "Marketplace-Checkout-btnPrimary" + (this.state.submitting ? " Marketplace-Checkout-btnPrimary--disabled" : "")
          }
          onClick={this.state.submitting ? undefined : this.confirmAndPay}
        >
          {this.state.submitting
            ? m.checkoutProcessing
            : vm.fill(m.checkoutConfirmAndGive, { amount: vm.monthlyLabel })}
        </div>
        <div className="Marketplace-Checkout-editLink" onClick={() => this.setState({ step: "card" })}>
          {m.checkoutEditCard}
        </div>
        <div className="Marketplace-Checkout-agreement">{m.checkoutAgreement}</div>
      </div>
    );
  }

  render3DSStep() {
    const m = this.props.vm.m;
    return (
      <div className="Marketplace-Checkout-card">
        <div className="Marketplace-Checkout-title">{m.checkoutBankTitle}</div>
        <p className="Marketplace-Checkout-sub">{m.checkoutBankSub}</p>
        <div className="Marketplace-Checkout-iframeWrap">
          <iframe className="Marketplace-Checkout-iframe" src={this.state.stripe3DS} title="3D Secure" />
        </div>
      </div>
    );
  }

  renderErrorStep() {
    const m = this.props.vm.m;
    return (
      <div className="Marketplace-Checkout-card">
        <div className="Marketplace-Checkout-title">{m.checkoutWrongTitle}</div>
        {SHOW_ERRORS ? (
          <ErrorPreview
            m={m}
            className="Marketplace-Checkout-sub"
            keys={["errCard", "errOrgLoad", "errPayment", "err3DSTimeout", "err3DSFailed"]}
          />
        ) : (
          <p className="Marketplace-Checkout-sub">{this.state.errMsg}</p>
        )}
        <div className="Marketplace-Checkout-btnPrimary" onClick={() => this.setState({ step: "card", errMsg: "" })}>
          {m.checkoutTryAgain}
        </div>
      </div>
    );
  }

  render() {
    const m = this.props.vm.m;
    const vm = this.props.vm;

    if (this.state.orgLoading) {
      return <div className="Marketplace-Checkout-loading">{m.checkoutLoading}</div>;
    }
    if (this.state.orgError) {
      return (
        <div className="Marketplace-Checkout-loading">{m.checkoutOrgError}</div>
      );
    }

    return (
      <div className="Marketplace-Checkout Marketplace-animIn">
        <div className="Marketplace-Checkout-back" onClick={vm.goAllocate}>
          {m.checkoutBack}
        </div>
        {this.state.step === "details" && this.renderDetailsStep()}
        {this.state.step === "card" && this.renderCardStep()}
        {this.state.step === "review" && this.renderReviewStep()}
        {this.state.step === "requires3DS" && this.render3DSStep()}
        {this.state.step === "error" && this.renderErrorStep()}
        {SHOW_ERRORS && <ErrorPreview m={m} className="Marketplace-Checkout-loading" keys={["checkoutOrgError"]} />}
        {SHOW_ERRORS && this.state.step !== "error" && this.renderErrorStep()}
      </div>
    );
  }
}

Checkout.propTypes = {
  vm: PropTypes.object.isRequired,
  stripe: PropTypes.object,
  elements: PropTypes.object,
};

function CheckoutWithStripe(props) {
  const stripe = useStripe();
  const elements = useElements();
  return <Checkout {...props} stripe={stripe} elements={elements} />;
}

const CheckoutWrapped = (props) => (
  <Elements stripe={stripePromise}>
    <CheckoutWithStripe {...props} />
  </Elements>
);

export default CheckoutWrapped;
